// @flow
import ApiClient from 'utils/ApiClient';
import Dispatcher from 'dispatcher';
import PaymentMethod from 'models/PaymentMethod';
import * as PaymentMethodActionTypes from 'action_types/PaymentMethodActionTypes';
import CurrentUserActions from 'actions/CurrentUserActions';
import CancelableRequests from 'utils/CancelableRequests';

const TYPE_ERRORS = [400, 403, 404, 500];
class PaymentMethodActions {
  cancelableRequests = new CancelableRequests();

  getPaymentMethodAdvance(id: string = ''): Promise<any> {
    return ApiClient.get({
      ...this.cancelableRequests.cancelableParams,
      path: `/payment_methods/home_applications/${id}`,
      success: response => {
        Dispatcher.dispatch({
          type: PaymentMethodActionTypes.GET_PAYMENT_METHOD_BY_MODEL,
          paymentMethod: response.map(
            paymentMetod => new PaymentMethod(paymentMetod)
          )
        });
      },
      error: error => {
        if (TYPE_ERRORS.includes(error.status)) {
          Dispatcher.dispatch({
            type: PaymentMethodActionTypes.PAYMENT_METHOD_ERROR,
            error: error
          });
        }
      }
    });
  }

  getPaymentMethodByModel(model_name: string = ''): Promise<any> {
    return ApiClient.get({
      ...this.cancelableRequests.cancelableParams,
      path: `/payment_methods/${model_name}.json`,
      success: response => {
        Dispatcher.dispatch({
          type: PaymentMethodActionTypes.GET_PAYMENT_METHOD_BY_MODEL,
          paymentMethod: response.map(
            paymentMetod => new PaymentMethod(paymentMetod)
          )
        });
      },
      error: error => {
        if (error.status === 404 || error.status === 500) {
          Dispatcher.dispatch({
            type: PaymentMethodActionTypes.PAYMENT_METHOD_ERROR,
            error: error
          });
        }
      }
    });
  }

  payApplicationByMethod(
    data: {},
    home_application_id: string,
    success?: () => void,
    errorFunction?: () => void
  ): Promise<any> {
    return ApiClient.post({
      path: `/home_applications/${home_application_id}/payments.json`,
      data: data,
      success: response => {
        Dispatcher.dispatch({
          type: PaymentMethodActionTypes.POST_PAY_APPLICATION_BY_METHOD,
          payment: response
        });
        CurrentUserActions.getCurrent();
        if (success !== undefined) {
          success();
        }
      },
      error: error => {
        if (
          error.status === 404 ||
          error.status === 400 ||
          error.status === 403
        ) {
          Dispatcher.dispatch({
            type: PaymentMethodActionTypes.PAYMENT_METHOD_ERROR_APP,
            error: {
              error: error.responseJSON
                ? error.responseJSON.message
                : error.message
            }
          });
          errorFunction();
        }
      }
    });
  }

  conektaKey(): Promise<any> {
    return ApiClient.get({
      path: `/utilities/conekta_public_key.json`,
      success: response => {
        Dispatcher.dispatch({
          type: PaymentMethodActionTypes.GET_CONEKTA_KEY,
          key: response
        });
      },
      skipApiUrl: true
    });
  }

  paySrPagoMethod(
    data: {},
    rent_id: string,
    rental_month_id: string,
    success?: () => void,
    errorFunction?: () => void
  ): Promise<any> {
    return ApiClient.post({
      path: `/rents/${rent_id}/rental_months/${rental_month_id}/rental_payments.json`,
      data: data,
      success: response => {
        Dispatcher.dispatch({
          type: PaymentMethodActionTypes.POST_PAY_BY_SR_PAGO,
          payment: response
        });
        if (success !== undefined) {
          success();
        }
      },
      error: error => {
        if (
          error.status === 404 ||
          error.status === 400 ||
          error.status === 500
        ) {
          Dispatcher.dispatch({
            type: PaymentMethodActionTypes.PAYMENT_METHOD_ERROR,
            error: {
              error: error.responseJSON
                ? error.responseJSON.message
                : 'Ocurrió un error al procesar el pago'
            }
          });
          errorFunction();
        }
      }
    });
  }

  payFirstPaymentByMethod(
    data: {},
    home_application_id: string,
    success?: () => void
  ): Promise<any> {
    return ApiClient.post({
      path: `/home_applications/${home_application_id}/payments.json`,
      data: data,
      success: response => {
        Dispatcher.dispatch({
          type: PaymentMethodActionTypes.POST_PAY_APPLICATION_BY_METHOD,
          payment: response
        });
        if (success !== undefined) {
          success();
        }
      },
      error: error => {
        if (error.status === 404) {
          Dispatcher.dispatch({
            type: PaymentMethodActionTypes.PAYMENT_METHOD_ERROR,
            error: undefined
          });
        }
      }
    });
  }

  getPdfByIdentifier(homeAppId: string, identifier: string): Promise<any> {
    let request = new XMLHttpRequest();
    request.responseType = 'blob';
    request.open(
      'GET',
      `/api/internal/home_applications/${homeAppId}/payment_reference_requests?identifier=${identifier}`
    );
    request.addEventListener('load', function() {
      let aTag = document.createElement('a');
      let url = URL.createObjectURL(request.response);
      aTag.href = url;
      aTag.download = `payment_reference_${homeAppId}.pdf`;
      aTag.click();
    });
    request.send();
  }

  payTicketAdvanceApplication(
    homeAppId: string,
    paymentRequest = {},
    success?: () => void,
    callback_error?: () => void
  ): Promise<any> {
    return ApiClient.post({
      path: `/home_applications/${homeAppId}/payment_reference_requests`,
      jsonData: paymentRequest,
      success: response => {
        Dispatcher.dispatch({
          type: PaymentMethodActionTypes.PAY_SPEI_ADVANCE_SUCCESS,
          payment: response
        });

        success && success();
      },
      error: error => {
        if (TYPE_ERRORS.includes(error.status)) {
          Dispatcher.dispatch({
            type: PaymentMethodActionTypes.PAY_BY_ADVANCE_ERROR,
            error: {
              error: error.responseJSON
                ? error.responseJSON.message
                : error.responseText
            }
          });

          callback_error && callback_error();
        }
      }
    });
  }

  payAdvanceApplication(
    data = {},
    success?: () => void,
    callback_error?: (message: string) => void
  ): Promise<any> {
    return ApiClient.post({
      path: `/home_applications/${data.home_application_id}/rents`,
      jsonData: data,
      success: response => {
        Dispatcher.dispatch({
          type: PaymentMethodActionTypes.PAY_BY_ADVANCE_SUCCESS,
          payment: response
        });
        if (success !== undefined) {
          success();
        }
      },
      error: error => {
        if (
          error.status === 400 ||
          error.status === 403 ||
          error.status === 500
        ) {
          Dispatcher.dispatch({
            type: PaymentMethodActionTypes.PAY_BY_ADVANCE_ERROR,
            error: {
              error: error.responseJSON
                ? error.responseJSON.message
                : error.responseText
            }
          });
          callback_error();
        }
      }
    });
  }

  cancelAllRequests() {
    this.cancelableRequests.cancelAllRequests();
  }

  payPhotoShooting = (
    home_id,
    photoshooting_id,
    data = {},
    success?: () => void,
    callback_error?: () => void
  ): Promise<any> => {
    return ApiClient.post({
      path: `/homes/${home_id}/photoshootings/${photoshooting_id}/payments.json`,
      data: data,
      success: response => {
        Dispatcher.dispatch({
          action: PaymentMethodActionTypes.POST_PAY_PHOTOSHOOTING,
          payment: response
        });
        if (success !== undefined) {
          success();
        }
      },
      error: error => {
        if (error.status === 400 || error.status === 403) {
          Dispatcher.dispatch({
            type: PaymentMethodActionTypes.PAY_PHOTOSHOOTING_ERROR,
            error: {
              error: error.responseJSON
                ? error.responseJSON.message
                : error.responseText
            }
          });
        }

        if (callback_error !== undefined) {
          callback_error();
        }
      }
    });
  };

  getPaymentMethodPhotoShooting(
    success?: () => void,
    error?: () => void
  ): Promise<any> {
    return ApiClient.get({
      path: `/payment_methods/photoshooting.json`,
      success: response => {
        Dispatcher.dispatch({
          type: PaymentMethodActionTypes.GET_PAYMENT_METHODS_FOOTOSHOOTING,
          paymentMethods: response
        });

        if (success !== undefined) {
          success();
        }
      },
      error: error
    });
  }

  getAvailableInterestFreeMonths() {
    return ApiClient.get({
      path: `/homie_configs/allowed.non.interests.months`
    }).then(interestFreeMonthsResponse =>
      interestFreeMonthsResponse.homie_config.config_value
        .split(',')
        .map(month => month.trim())
    );
  }
}

export default new PaymentMethodActions();
